// Datos de Inventario v3: la lista del lente, tiendas y zonas del retailer y el stock de tarjetas PCF.
import { useQuery } from '@tanstack/react-query';
import {
    inventoryLensApi,
    type InventoryLensPeriod,
} from '@/api/services/retail-catalog/backoffice';
import { getCardStock } from '@/api/services/retail-cards/retail-cards.client';
import { listStores, listZones } from '@/api/services/retail-org/retail-org.client';
import { ApiError } from '@/api/errors';
import { CARD_STOCK_QUERY_KEY, INVENTORY_QUERY_KEY } from './useInventarioLens';

/** `?filter=list` del periodo y la tienda; sin tienda es el consolidado. */
export function useInventoryList(period: InventoryLensPeriod, storeId: string | undefined) {
    return useQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'list', period, storeId ?? null],
        queryFn: () => inventoryLensApi.getList({ period, storeId }),
        staleTime: 60_000,
    });
}

/** Las tiendas del retailer del token, para el filtro de tienda y la distribución. */
export function useRetailerStores(retailerId: string | null) {
    return useQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'stores', retailerId],
        queryFn: () => listStores(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 300_000,
    });
}

/** Las zonas del retailer, para nombrar la zona de cada tienda desde el padrón. */
export function useRetailerZones(retailerId: string | null) {
    return useQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'zones', retailerId],
        queryFn: () => listZones(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 300_000,
    });
}

/** Saldo de tarjetas de una tienda. Una tienda sin lotes responde 404: es inventario vacío. */
export async function availableCards(retailerId: string, storeId: string): Promise<number> {
    try {
        const stock = await getCardStock(retailerId, storeId, { limit: 1 });
        return stock.balance.available;
    } catch (error) {
        if (error instanceof ApiError && error.code === 'CARD_STOCK_NOT_FOUND') return 0;
        throw error;
    }
}

/** Stock de tarjetas PCF por tienda para la fila PCF de la lista. */
export function useCardStock(retailerId: string | null, storeIds: readonly string[]) {
    return useQuery({
        queryKey: [...CARD_STOCK_QUERY_KEY, retailerId, storeIds],
        queryFn: async () => {
            const counts = await Promise.all(
                storeIds.map((storeId) => availableCards(retailerId ?? '', storeId)),
            );
            return new Map(storeIds.map((storeId, index) => [storeId, counts[index]]));
        },
        enabled: retailerId !== null && storeIds.length > 0,
        staleTime: 60_000,
        retry: false,
    });
}
